import React, { useEffect, useRef } from "react";
import { Controller, useWatch } from "react-hook-form";
import Grid from "@mui/material/Grid";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  OutlinedInput,
  Typography,
  Divider,
} from "@mui/material";
import { get } from "lodash";
import CustomTextInput from "../CustomTextInput";
import SingleDatePicker from "./SingleDatePicker";
import dayjs from "dayjs";
import TransactionTypeSelect from "./TransactionTypeSelect";
import { isSummaryTransactionType } from "../../resources/common/isSummaryTransactionType";
import {isExportTransactionType} from "../../resources/common/isExportTransactionType";
import CountrySelect from "./CountrySelect";
import OMNSubdivisionSelect from "./OMNSubdivisionSelect";
import {OMN_COUNTRY_CODE} from "../../constants/defaults";
import {transactionTypePositionCheck} from "../../utils/helper";
import {isContinuousSupplyTransactionType} from "../../resources/common/isContinuousSupplyTransactionType";
import {isIOGTransactionType} from "../../resources/common/isIOGTransactionType";
import {isEcommerceTransactionType} from "../../resources/common/isEcommerceTransactionType";
import IncotermsSelect from "./IncotermsSelect";

const frequencyOptions = [
  "Daily",
  "Weekly",
  "Once in 15 days",
  "Monthly",
  "Once in 45 days",
  "Once in 60 days",
  "Quarterly",
  "Yearly",
  "Half-Yearly",
  "Others",
];

const TransactionTypeFields = ({
  control,
  errors,
  readOnly = false,
  transactionType,
  setValue,
}) => {
  const values = useWatch({ control });
  transactionType = transactionType || values?.transaction_type || "";
  const isSummary = isSummaryTransactionType(transactionType);
  const isContinuousSupply = isContinuousSupplyTransactionType(transactionType);
  const isExport = isExportTransactionType(transactionType);
  const isIOG = isIOGTransactionType(transactionType);
  const isEcommerce = isEcommerceTransactionType(transactionType);

  const wasSummary = useRef(isSummary);
  const wasExport = useRef(isExport);
  const wasContinuousSupply = useRef(isContinuousSupply);
  const wasIOG = useRef(isIOG);
  const wasEcommerce = useRef(isEcommerce);

  const hasValue = (val) => {
    return val !== undefined && val !== null && val !== "";
  };


  const hasBillingPeriodData =
    hasValue(values?.invoice_period_start_date) ||
    hasValue(values?.invoice_period_end_date) ||
    hasValue(values?.invoice_period_description);

  const showInvoicePeriod = isSummary || hasBillingPeriodData || isContinuousSupply;

  const showDeliveryDetails =
      transactionTypePositionCheck(transactionType, 7) || transactionTypePositionCheck(transactionType, 8) || transactionTypePositionCheck(transactionType, 13) || transactionTypePositionCheck(transactionType, 12);

  const invoicePeriodStartDate = useWatch({
    control,
    name: "invoice_period_start_date",
  });

  useEffect(() => {
    if (!readOnly && setValue && wasSummary.current && !isSummary || !readOnly && setValue && wasContinuousSupply.current && !isContinuousSupply) {
      setValue("invoice_period_start_date", null);
      setValue("invoice_period_end_date", null);
      setValue("invoice_period_description", null);
    }
    wasSummary.current = isSummary;
    wasContinuousSupply.current = isContinuousSupply;
  }, [isSummary, isContinuousSupply, readOnly, setValue]);

  useEffect(() => {
    if (!readOnly && setValue && wasExport.current && !isExport) {
      setValue("delivery_party_name", null);
      setValue("delivery_date", null);
      setValue("delivery_additional_street_name", null);
      setValue("delivery_street_name", null);
      setValue("delivery_address_line", null);
      setValue("delivery_city_name", null);
      setValue("delivery_country_code", null);
      setValue("delivery_country_subentity", null);
      setValue("delivery_postal_zone", null);
      setValue("delivery_incoterms", null);
      setValue("delivery_party_id", null);
    }
    wasExport.current = isExport;
  }, [isExport, readOnly, setValue]);

  const handleCountryChange = (value) => {
    setValue('country_subentity', value?.code === OMN_COUNTRY_CODE ? null : '');
  };

  const deliveryCountry = useWatch({ control, name: 'country_code' });

  return (
    <>
      <Grid size={{ xs: 12, sm: 4, md: 4 }}>
        <TransactionTypeSelect
          id="trasaction_type"
          name="transaction_type"
          label="Transaction Type Code"
          control={control}
          errors={errors}
          readOnly={readOnly}
          onChange={() => {}}
          helperText=""
        />
      </Grid>

      {showDeliveryDetails && (
          <>
            <Grid container spacing={2} size={{ xs: 12, sm: 12, md: 12 }}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6">Delivery Details</Typography>
                <Divider />
              </Grid>
            </Grid>
            <Grid container spacing={2} size={{ xs: 12, sm: 12, md: 12 }}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <CustomTextInput
                    data-testid="delivery-party-name-input"
                    name="delivery_party_name"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label="Delivery Party Name"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <SingleDatePicker
                    data-testid="delivery_date-input"
                    name="delivery_date"
                    label="Delivery Date"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    minDate={undefined}
                    maxDate={undefined}
                    helperText=""
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <CustomTextInput
                    data-testid="additional-street-name-input"
                    name="delivery_additional_street_name"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label="Additional Street Name"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <CustomTextInput
                    data-testid="address_line-input"
                    name="delivery_address_line"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label="Address Line"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <CustomTextInput
                    data-testid="street-name-input"
                    name="delivery_street_name"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label="Street Name"
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} size={{ xs: 12, sm: 12, md: 12 }}>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <CustomTextInput
                    data-testid="city_name-input"
                    name="delivery_city_name"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label="City Name"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <CountrySelect
                    data-testid="country_code-select"
                    name="delivery_country_code"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label="Country"
                    onChange={handleCountryChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                {deliveryCountry?.code === OMN_COUNTRY_CODE ? (
                    <OMNSubdivisionSelect
                        data-testid="country_subentity-select"
                        name="delivery_country_subentity"
                        control={control}
                        errors={errors}
                        readOnly={readOnly}
                    />
                ) : (
                    <CustomTextInput
                        data-testid="country_subentity-input"
                        name="delivery_country_subentity"
                        control={control}
                        errors={errors}
                        readOnly={readOnly}
                        label="Country Subdivision"
                    />
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <CustomTextInput
                    data-testid="postal_zone-input"
                    name="delivery_postal_zone"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                    label="Postal Zone"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Controller
                    name="delivery_incoterms"
                    control={control}
                    render={({ field }) => (
                      <IncotermsSelect
                          control={control}
                          errors={errors}
                          name="delivery_incoterms"
                          readOnly={readOnly}
                          helperText=""
                          onChange={(newCode) => {
                            field.onChange(newCode);
                          }}
                      />
                    )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <CustomTextInput
                    data-testid="party-id-input"
                    label="Party ID"
                    name="delivery_party_id"
                    control={control}
                    errors={errors}
                    readOnly={readOnly}
                />
              </Grid>
            </Grid>
          </>
      )}

      {showInvoicePeriod && (
        <Grid container spacing={2} size={{ xs: 12, sm: 12, md: 12 }}>
          {(isSummary ||
            hasValue(values?.invoice_period_start_date) ||
            !readOnly) && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SingleDatePicker
                data-testid="invoice-period-start-date-input"
                name="invoice_period_start_date"
                label="Invoicing Period Start Date"
                control={control}
                errors={errors}
                readOnly={readOnly}
                minDate={undefined}
                maxDate={undefined}
                helperText=""
              />
            </Grid>
          )}

          {(isSummary ||
            hasValue(values?.invoice_period_end_date) ||
            !readOnly) && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SingleDatePicker
                data-testid="invoice-period-end-date-input"
                name="invoice_period_end_date"
                label="Invoicing Period End Date"
                control={control}
                errors={errors}
                readOnly={readOnly}
                minDate={
                  invoicePeriodStartDate
                    ? dayjs(invoicePeriodStartDate)
                    : undefined
                }
                maxDate={undefined}
                helperText=""
              />
            </Grid>
          )}

          {(isSummary ||
            hasValue(values?.invoice_period_description) ||
            !readOnly) && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Controller
                name="invoice_period_description"
                control={control}
                render={({ field }) => {
                  const errorMessage = get(
                    errors,
                    "invoice_period_description",
                    {},
                  )?.message;
                  return (
                    <FormControl
                      fullWidth
                      error={!!errorMessage}
                      variant="outlined"
                      size="small"
                    >
                      <InputLabel id="invoice-period-description-label">
                        Frequency of Billing
                      </InputLabel>
                      <Select
                        labelId="invoice-period-description-label"
                        id="invoice-period-description"
                        data-testid="invoice-period-description"
                        value={field.value || ""}
                        onChange={field.onChange}
                        input={<OutlinedInput label="Frequency of Billing" />}
                        disabled={readOnly}
                      >
                        {frequencyOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                      {errorMessage && (
                        <FormHelperText>{errorMessage}</FormHelperText>
                      )}
                    </FormControl>
                  );
                }}
              />
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};

export default TransactionTypeFields;
